/** Writing and reading a `SaveFile`.
 *
 * The round trip is measured by hash, not by deep equality: `hashState(load(
 * save(s))) === hashState(s)` is what determinism means here, and it is the
 * only comparison that would notice a `Uint16Array` coming back as a plain
 * array of the same numbers.
 *
 * Reading is a pipeline with one order: parse text, migrate to the current
 * format (I13), validate the shape and sweep for presentation fields (I11),
 * then decode the typed arrays. Validating before migrating would refuse every
 * old save; decoding before validating would build a `GameState` out of a file
 * that has no business becoming one.
 */

import type { ContentRegistry } from "../content/registry.js";
import type { StateHash } from "../hash/index.js";
import { hashState } from "../hash/index.js";
import { migrateToCurrent } from "../migrations/index.js";
import type { Result } from "../result.js";
import { Err, Ok } from "../result.js";
import type { GameState } from "../state.js";
import { decodeTree, encodeTree, parseJson, serialize, type Json } from "./codec.js";
import { validateSaveTree } from "./schema.js";
import type { PersistError, SaveFile } from "./types.js";
import { SAVE_FORMAT_VERSION, SAVE_KIND, malformedFile } from "./types.js";

// ------------------------------------------------------------------- write

/**
 * The envelope holds four fields and no fifth. Everything a save screen wants
 * to show - the day, whose turn it is, which towns are held - is already in the
 * state and is read from there, so there is no metadata block for a camera
 * position to move into later (I11).
 */
export function makeSave(state: GameState): SaveFile {
  return {
    kind: SAVE_KIND,
    formatVersion: SAVE_FORMAT_VERSION,
    contentVersion: state.contentVersion,
    state,
  };
}

/** Canonical JSON text. Two saves of the same state are the same bytes. */
export function serializeSave(save: SaveFile): string {
  return serialize(save);
}

/** The whole write path in one call. */
export function saveGame(state: GameState): string {
  return serializeSave(makeSave(state));
}

/** JSON-safe data rather than text, for a host that would rather post it to a
 *  worker or hand it to IndexedDB than stringify it. */
export function saveToJson(state: GameState): Json {
  return encodeTree(makeSave(state));
}

/** The checkpoint hash of a state, for callers that do not want the hash module. */
export const stateHash = (state: GameState): StateHash => hashState(state);

// -------------------------------------------------------------------- read

export interface LoadedSave {
  readonly save: SaveFile;
  /** Non-empty when the file was written by an older build. */
  readonly migrated: readonly string[];
  readonly fromFormatVersion: number;
}

export interface LoadOpts {
  /**
   * Refuse a save recorded against different content. Off by default: I13's
   * bargain is that a *save* survives a content bump (a replay does not, Section
   * 5.4), so a host that has an alias table says so rather than being told.
   */
  readonly content?: ContentRegistry;
  readonly requireContentVersion?: boolean;
}

/** Parse, migrate, validate, decode. Every failure is returned. */
export function parseSave(text: string, opts?: LoadOpts): Result<LoadedSave, PersistError> {
  const parsed = parseJson(text);
  if (!parsed.ok) return parsed;
  return loadSaveTree(parsed.value, opts);
}

/** The same pipeline from already-parsed data (a worker message, IndexedDB). */
export function loadSaveTree(raw: unknown, opts?: LoadOpts): Result<LoadedSave, PersistError> {
  const migrated = migrateToCurrent(raw);
  if (!migrated.ok) return migrated;

  const valid = validateSaveTree(migrated.value.tree, SAVE_FORMAT_VERSION);
  if (!valid.ok) return valid;

  const decoded = decodeTree(valid.value["state"], "$.state");
  if (!decoded.ok) return decoded;

  const state = decoded.value as GameState;
  const contentVersion = valid.value["contentVersion"] as string;

  if (opts?.requireContentVersion === true && opts.content !== undefined
      && opts.content.contentVersion !== contentVersion) {
    return Err({
      kind: "CONTENT_MISMATCH", expected: opts.content.contentVersion, found: contentVersion,
    });
  }

  return Ok({
    save: { kind: SAVE_KIND, formatVersion: SAVE_FORMAT_VERSION, contentVersion, state },
    migrated: migrated.value.applied,
    fromFormatVersion: migrated.value.from,
  });
}

/** The read path when only the state is wanted. */
export function loadGame(text: string, opts?: LoadOpts): Result<GameState, PersistError> {
  const r = parseSave(text, opts);
  return r.ok ? Ok(r.value.save.state) : r;
}

/**
 * A save is *self-checking*: it holds a whole state, so the only way to be sure
 * it survived the trip is to hash what came back and compare it to what went
 * out. A host that cares (autosave, a bug report attachment) calls this.
 */
export function verifyRoundTrip(state: GameState): Result<StateHash, PersistError> {
  const text = saveGame(state);
  const back = loadGame(text);
  if (!back.ok) return back;
  const before = hashState(state);
  const after = hashState(back.value);
  if (before !== after) {
    return Err(malformedFile(`save round trip changed the state hash: ${before} -> ${after}`));
  }
  return Ok(before);
}
